import React, { useState, useEffect } from 'react';

const ListeningGenerationModal = ({ 
  isOpen, 
  onClose, 
  settings, 
  onSettingsChange, 
  onConfirm, 
  title = "수준별 듣기 문항 생성",
  isGenerating = false
}) => {
  const [progress, setProgress] = useState(0);
  const [passages, setPassages] = useState([]);
  const [loadingPassages, setLoadingPassages] = useState(false);
  const [selectedPassage, setSelectedPassage] = useState(null);

  // 생성 시작 시 진행바 애니메이션 효과
  useEffect(() => {
    if (isGenerating) {
      setProgress(0);
      // 첫 번째 페이즈: 빠른 시작 (0-30%)
      const phase1 = setTimeout(() => setProgress(30), 300);
      // 두 번째 페이즈: 중간 속도 (30-60%)
      const phase2 = setTimeout(() => setProgress(60), 2000);
      // 세 번째 페이즈: 느린 진행 (60-85%)
      const phase3 = setTimeout(() => setProgress(85), 8000);
      // 네 번째 페이즈: 매우 느린 진행 (85-95%)
      const phase4 = setTimeout(() => setProgress(95), 15000);

      return () => {
        clearTimeout(phase1);
        clearTimeout(phase2);
        clearTimeout(phase3);
        clearTimeout(phase4);
      };
    } else if (progress > 0) {
      // 완료 시 100%로 애니메이션
      setProgress(100);
      const resetTimeout = setTimeout(() => setProgress(0), 1000);
      return () => clearTimeout(resetTimeout);
    }
  }, [isGenerating]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    onSettingsChange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 구글 시트에서 듣기마스터 지문 가져오기
  const fetchListeningPassages = async () => {
    if (!settings.sheetUrl) {
      alert('먼저 학습 로그 시트 URL을 입력해주세요.');
      return;
    }

    setLoadingPassages(true);
    try {
      const spreadsheetId = extractSpreadsheetId(settings.sheetUrl);
      const response = await fetch('/api/get-listening-passages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          spreadsheetId,
          sheetName: '듣기마스터'
        }),
      });

      const data = await response.json();
      if (data.success) {
        setPassages(data.passages);
        if (data.passages.length === 0) {
          alert('듣기마스터 시트에 지문이 없습니다.');
        }
      } else {
        throw new Error(data.error || '지문을 가져오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Error fetching passages:', error);
      alert('지문을 가져오는데 실패했습니다: ' + error.message);
    } finally {
      setLoadingPassages(false);
    }
  };

  // 구글 시트 URL에서 스프레드시트 ID 추출
  const extractSpreadsheetId = (url) => {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isGenerating ? '듣기 문항 생성 중...' : title}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isGenerating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
              {/* 학교급 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              학교급
            </label>
            <select
              value={settings.schoolLevel}
              onChange={(e) => handleInputChange('schoolLevel', e.target.value)}
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">선택하세요</option>
              <option value="elementary">초등학교</option>
              <option value="middle">중학교</option>
              <option value="high">고등학교</option>
              <option value="advanced">심화과정</option>
            </select>
          </div>

          {/* 학년 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              학년
            </label>
            <select
              value={settings.grade}
              onChange={(e) => handleInputChange('grade', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!settings.schoolLevel || isGenerating}
            >
              <option value="">선택하세요</option>
              {settings.schoolLevel === 'elementary' && (
                <>
                  <option value="3">3학년</option>
                  <option value="4">4학년</option>
                  <option value="5">5학년</option>
                  <option value="6">6학년</option>
                </>
              )}
              {settings.schoolLevel === 'middle' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {settings.schoolLevel === 'high' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {settings.schoolLevel === 'advanced' && (
                <>
                  <option value="1">1단계</option>
                  <option value="2">2단계</option>
                  <option value="3">3단계</option>
                </>
              )}
            </select>
          </div>

          {/* 주제 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              듣기 주제 (선택사항)
            </label>
            <select
              value={settings.topic}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">AI가 자동으로 선택</option>
              <option value="일상생활">일상생활</option>
              <option value="학교생활">학교생활</option>
              <option value="취미활동">취미활동</option>
              <option value="가족과 친구">가족과 친구</option>
              <option value="여행과 문화">여행과 문화</option>
              <option value="환경보호">환경보호</option>
              <option value="건강과 운동">건강과 운동</option>
              <option value="음식과 요리">음식과 요리</option>
              <option value="쇼핑과 소비">쇼핑과 소비</option>
              <option value="미래와 꿈">미래와 꿈</option>
            </select>
          </div>

          {/* 학습 로그 시트 URL (선택사항) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              학습 로그 시트 URL (선택사항)
            </label>
            <input
              type="url"
              value={settings.sheetUrl}
              onChange={(e) => handleInputChange('sheetUrl', e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              disabled={isGenerating}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              학습 결과를 기록할 구글시트 URL을 입력하세요 (생략 가능)
            </p>
          </div>

          {/* 학년별 듣기 설정 정보 표시 */}
          {settings.schoolLevel && settings.grade && !isGenerating && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">{settings.schoolLevel === 'elementary' ? '초등학교' : settings.schoolLevel === 'middle' ? '중학교' : settings.schoolLevel === 'high' ? '고등학교' : '심화과정'} {settings.grade}학년 듣기 설정</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• 자동 생성되는 듣기 유형: {getListeningTypes(settings.schoolLevel, settings.grade)}</li>
                    <li>• 최대 재생 횟수: {getMaxPlays(settings.schoolLevel, settings.grade)}회</li>
                    <li>• 목표 정답률: {getTargetAccuracy(settings.schoolLevel, settings.grade)}</li>
                    <li>• 예상 지문 길이: {getScriptLength(settings.schoolLevel, settings.grade)}</li>
                    <li>• TTS 음성: 85% 속도, 고품질 음성</li>
                    <li>• 배속 조절 가능 (0.75x ~ 1.5x)</li>
                    <li>• 단독 화자 형태 (선생님, 학생, 안내원 등)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

              {/* 생성 중 표시 */}
              {isGenerating && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center mb-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-2"></div>
                    <span className="font-medium text-green-800">듣기 문항을 생성하고 있습니다...</span>
                  </div>
                  
                  {/* 진행바 */}
                  <div className="mb-2">
                    <div className="flex justify-between text-sm text-green-700 mb-1">
                      <span>생성 진행률</span>
                      <span className="font-mono">{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-3 overflow-hidden shadow-inner">
                      <div 
                        className="h-full bg-gradient-to-r from-green-400 via-green-500 to-green-600 rounded-full transition-all duration-700 ease-out shadow-sm"
                        style={{
                          width: `${progress}%`,
                          background: progress > 50 
                            ? 'linear-gradient(to right, #10b981, #059669, #047857)' 
                            : 'linear-gradient(to right, #34d399, #10b981, #059669)'
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-green-700">
                    <span className="animate-pulse">
                      {progress < 30 && '🚀 생성 준비 중...'}
                      {progress >= 30 && progress < 60 && '🤖 GPT가 지문을 작성하고 있습니다...'}
                      {progress >= 60 && progress < 85 && '🎵 TTS가 음성 파일을 생성하고 있습니다...'}
                      {progress >= 85 && progress < 100 && '📝 문항을 정리하고 있습니다...'}
                      {progress >= 100 && '✅ 생성이 완료되었습니다!'}
                    </span>
                  </p>
                </div>
              )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isGenerating}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={!settings.schoolLevel || !settings.grade || isGenerating}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isGenerating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {isGenerating ? '생성 중...' : '듣기 문항 생성'}
          </button>
        </div>
      </div>
    </div>
  );
};

// 헬퍼 함수들 (단독 화자 중심으로 수정)
function getListeningTypes(schoolLevel, grade) {
  const gradeNum = parseInt(grade);
  
  if (schoolLevel === 'elementary') {
    if (gradeNum <= 4) return '짧은이야기, 안내방송';
    return '설명, 발표, 이야기';
  } else if (schoolLevel === 'middle') {
    if (gradeNum === 1) return '수업설명, 발표, 뉴스';
    if (gradeNum === 2) return '강의, 발표, 안내';
    return '강의, 연설, 리포트';
  } else if (schoolLevel === 'high') {
    if (gradeNum === 1) return '강의, 연설, 프레젠테이션';
    if (gradeNum === 2) return '학술발표, 강연, 토론';
    return '대학강의, 전문강연';
  }
  
  return '수업설명, 발표';
}

function getMaxPlays(schoolLevel, grade) {
  const gradeNum = parseInt(grade);
  
  if (schoolLevel === 'elementary' && gradeNum <= 4) {
    return 3;
  }
  
  return 2;
}

function getTargetAccuracy(schoolLevel, grade) {
  const gradeNum = parseInt(grade);
  
  if (schoolLevel === 'elementary') {
    if (gradeNum <= 4) return '80-90%';
    return '70-80%';
  } else if (schoolLevel === 'middle') {
    return '60-75%';
  } else if (schoolLevel === 'high') {
    return '50-65%';
  }
  
  return '65-75%';
}

function getScriptLength(schoolLevel, grade) {
  const gradeNum = parseInt(grade);
  
  if (schoolLevel === 'elementary') {
    return gradeNum <= 4 ? '30-50단어' : '50-80단어';
  } else if (schoolLevel === 'middle') {
    return '80-120단어';
  } else if (schoolLevel === 'high') {
    return '120-180단어';
  }
  
  return '80-120단어';
}

export default ListeningGenerationModal;